import { inject } from '@angular/core';
import {
  CanActivateFn,
  Router,
  type ActivatedRouteSnapshot,
  type GuardResult,
  type RouterStateSnapshot,
} from '@angular/router';
import { catchError, map, of, take } from 'rxjs';
import { LoadCommerceLandingUseCase } from '../../../application/commerce-registry/use-cases/load-commerce-landing.use-case';
import type { CommerceLanding } from '../../../domain/commerce-registry/models/commerce-landing.model';
import { ENVIRONMENT } from '../../../../environments/environment';

/**
 * Resolves `programa` / `programa_slug` from the full navigation URL (same as {@link commerceLandingLoad$}).
 */
function programaSlugFromState(router: Router, state: RouterStateSnapshot): string | null {
  const tree = router.parseUrl(state.url);
  const raw = tree.queryParams['programa'] ?? tree.queryParams['programa_slug'];
  if (typeof raw !== 'string') {
    return null;
  }
  const t = raw.trim();
  return t.length > 0 ? t : null;
}

/** `comercioSlug` lives on the parent segment; walk up so we resolve it regardless of which snapshot Angular passes. */
function readComercioSlugFromRouteTree(route: ActivatedRouteSnapshot): string | null {
  let current: ActivatedRouteSnapshot | null = route;
  while (current) {
    const slug = current.paramMap.get('comercioSlug')?.trim();
    if (slug) {
      return slug;
    }
    current = current.parent;
  }
  return null;
}

/**
 * Ensures Firebase RTDB has a landing for `comercio/:comercioSlug` before activating commerce routes.
 * When no row exists and mocks are off, redirects to `/404`.
 */
export const registryCommerceLandingExistsGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const load = inject(LoadCommerceLandingUseCase);
  const slug = readComercioSlugFromRouteTree(route);
  if (!slug) {
    return router.createUrlTree(['/404']);
  }
  const programa = programaSlugFromState(router, state);
  return load.execute(slug, programa).pipe(
    take(1),
    map((landing) => resolveGuardResult(router, landing)),
    catchError(() => of(resolveGuardResult(router, null))),
  );
};

function resolveGuardResult(router: Router, landing: CommerceLanding | null): GuardResult {
  if (landing != null) {
    return true;
  }
  if (ENVIRONMENT.mockEnabled) {
    return true;
  }
  return router.createUrlTree(['/404']);
}
